import { useCallback } from 'react';

import { useLoginOrSignupPageState } from 'src/app/pages/loginOrSignup/pageContext';
import {
	useCanSubmitForm,
	useLoginOrSignupState,
	useLoginOrSignupTracking,
} from 'src/app/pages/loginOrSignup/shared/hooks';

type StepData = { firstName: string; lastName: string };
type StepDataErrors = Record<`${keyof StepData}Error`, string | null>;

type Result = ModelHookResult<
	StepData & StepDataErrors,
	{
		setStepData: (data: Partial<StepData>) => void;
		submitStep: () => Promise<void>;
		trackStepView: () => void;
	}
>;

export function useNameStep(): Result {
	const { gotoNextStep } = useLoginOrSignupState();
	const [{ firstName, lastName, formErrors }, { setFormData }] = useLoginOrSignupPageState();
	const { trackLoginNewUserNameView } = useLoginOrSignupTracking();

	const canSubmit = useCanSubmitForm();

	const submitStep = useCallback(async () => {
		if (!canSubmit) return;
		await gotoNextStep();
	}, [gotoNextStep, canSubmit]);

	return {
		data: {
			firstName,
			firstNameError: formErrors?.firstName ?? null,
			lastName,
			lastNameError: formErrors?.lastName ?? null,
		},
		actions: {
			setStepData: useCallback(
				(data) => {
					if (Object.keys(data).length) {
						setFormData({ firstName, lastName, ...data });
					}
				},
				[setFormData, firstName, lastName],
			),
			submitStep,
			trackStepView: trackLoginNewUserNameView,
		},
	};
}
